import { CallDto } from '@act/shared/data-transfer-objects';
import type { Slice, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { WebsocketEventActions } from '../types/websocketTypes';
import { addToEffectsMiddleware } from '../utils/effectsMiddleware.util';
import { verifyUser } from './actingUserSlice.slice';
import { getUsersByIds } from '../thunks/users.thunk';
import { selectUsersMap } from './usersSlice.slice';
import { getActiveCalls } from './callsSliceThunks.thunk';
import type { callsSliceReducers } from './callsSliceReducers.util';
import { CallState } from '../types/callsTypes';
import {
  CallSocketEventTypes,
  CallUpdatedTypes,
} from '@act/shared/socket-events';

type CallsSlice = Slice<CallState, typeof callsSliceReducers, 'calls'>;
type UsersMapSelectorState = Parameters<typeof selectUsersMap>[0];
type EffectDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

/**
 * Fetches users referenced by calls when they are missing from Redux.
 * @param calls Single call or array of calls to inspect.
 * @param state Current Redux root state.
 * @param dispatch Redux dispatch function.
 */
function fetchMissingUsersFromCalls(
  calls: CallDto | CallDto[],
  state: UsersMapSelectorState,
  dispatch: EffectDispatch
): void {
  const callsArray = Array.isArray(calls) ? calls : [calls];
  if (callsArray.length === 0) {
    return;
  }

  const usersMap = selectUsersMap(state);
  const missingUserIds = new Set<string>();

  callsArray.forEach(call => {
    if (call.subjectId && !usersMap[call.subjectId]) {
      missingUserIds.add(call.subjectId);
    }

    if (call.originator?.userId && !usersMap[call.originator.userId]) {
      missingUserIds.add(call.originator.userId);
    }

    if (call.receiver?.userId && !usersMap[call.receiver.userId]) {
      missingUserIds.add(call.receiver.userId);
    }

    call.connections?.forEach(connection => {
      if (connection.userId && !usersMap[connection.userId]) {
        missingUserIds.add(connection.userId);
      }
    });
  });

  if (missingUserIds.size > 0) {
    void dispatch(
      getUsersByIds({ params: { ids: Array.from(missingUserIds) } })
    );
  }
}

/**
 * Registers calls slice side effects after the slice has been created.
 * @param callsSlice Initialized calls Redux slice.
 */
export function registerCallsSliceEffects(callsSlice: CallsSlice): void {
  addToEffectsMiddleware(addEffect => {
    addEffect({
      action: verifyUser.fulfilled,
      effect: async ({ dispatch }) => {
        dispatch(getActiveCalls({}));
      },
    });

    addEffect({
      action: getActiveCalls.fulfilled,
      effect: async ({ dispatch, action, state }) => {
        if (!action.payload || action.payload.length === 0) return;
        fetchMissingUsersFromCalls(action.payload, state, dispatch);
      },
    });

    addEffect({
      action: callsSlice.actions.justInTimeCallUpdated,
      effect: async ({ dispatch, action, state }) => {
        const call = action.payload?.call;
        if (!call) return;
        fetchMissingUsersFromCalls(call, state, dispatch);
      },
    });

    addEffect({
      action: callsSlice.actions.callCreated,
      effect: async ({ dispatch, action, state }) => {
        const call = action.payload?.call;
        if (!call) return;
        fetchMissingUsersFromCalls(call, state, dispatch);
      },
    });
  });
}

/**
 * Builds websocket action bindings for the calls slice.
 * @param callsSlice Initialized calls Redux slice.
 * @returns Websocket event action mappings for call updates.
 */
export function createCallsSliceWebsocketActions(
  callsSlice: CallsSlice
): WebsocketEventActions[] {
  return [
    {
      type: CallUpdatedTypes.CALL_UPDATED,
      action: callsSlice.actions.callUpdated,
      channel: 'baseSocket',
    },
    {
      type: CallUpdatedTypes.CALL_CONNECTION_UPDATED,
      action: callsSlice.actions.callConnectionUpdated,
      channel: 'baseSocket',
    },
    {
      type: CallSocketEventTypes.INCOMING_CALL_CREATED,
      action: callsSlice.actions.callCreated,
      channel: 'baseSocket',
    },
    {
      type: CallSocketEventTypes.OUTGOING_CALL_CREATED,
      action: callsSlice.actions.callCreated,
      channel: 'baseSocket',
    },
    {
      type: CallSocketEventTypes.CALL_UPDATED,
      action: callsSlice.actions.justInTimeCallUpdated,
      channel: 'baseSocket',
    },
    {
      type: CallUpdatedTypes.SUGGESTED_ACTIONS_UPDATED,
      action: callsSlice.actions.suggestedActionsUpdated,
      channel: 'baseSocket',
    },
  ];
}
